import React, { useState, useEffect } from "react";
import { BASE_URL } from "../utils/config";
import { getAuthHeaders } from "../utils/auth";
import "../styles/UsersManagement.css";

const UsersManagement = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const headers = getAuthHeaders(false);
      const res = await fetch(`${BASE_URL}/users`, {
        headers,
      });

      if (res.ok) {
        const data = await res.json();
        if (data.success) {
          setUsers(data.data || []);
        } else {
          setError(data.message || "Failed to fetch users");
        }
      } else {
        setError("Failed to fetch users");
      }
    } catch (error) {
      setError("Error loading users");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (userId) => {
    try {
      const headers = getAuthHeaders(false);
      const res = await fetch(`${BASE_URL}/users/${userId}`, {
        method: "DELETE",
        headers,
      });

      if (res.ok) {
        setUsers(users.filter((user) => user._id !== userId));
        setDeleteConfirm(null);
      } else {
        alert("Failed to delete user");
      }
    } catch (error) {
      alert("Error deleting user");
    }
  };

  const toggleRole = async (user) => {
    if (user.role === 'admin') {
      alert("Cannot change admin role");
      return;
    }

    try {
      const newRole = user.role === 'user' ? 'admin' : 'user';
      const headers = getAuthHeaders(true);
      const res = await fetch(`${BASE_URL}/users/${user._id}`, {
        method: "PUT",
        headers,
        body: JSON.stringify({ role: newRole }),
      });

      if (res.ok) {
        fetchUsers();
      } else {
        alert("Failed to update user role");
      }
    } catch (error) {
      alert("Error updating user role");
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="admin-loader"></div>
        <p>Loading users...</p>
      </div>
    );
  }

  return (
    <div className="users-management">
      <div className="users-header">
        <h3>All Users ({users.length})</h3>
        <button className="refresh-btn" onClick={fetchUsers}>
          <i className="ri-refresh-line"></i> Refresh
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {users.length === 0 ? (
        <div className="empty-state">
          <i className="ri-user-line"></i>
          <h3>No Users Found</h3>
          <p>No users have registered yet.</p>
        </div>
      ) : (
        <div className="users-table-container">
          <table className="users-table">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>Joined</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user._id}>
                  <td>
                    <div className="user-info">
                      <i className="ri-user-line"></i>
                      {user.username}
                    </div>
                  </td>
                  <td>{user.email}</td>
                  <td>
                    <span className={`role-badge ${user.role === 'admin' ? 'admin' : 'user'}`}>
                      {user.role === 'admin' ? (
                        <>
                          <i className="ri-shield-star-line"></i> Admin
                        </>
                      ) : (
                        <>
                          <i className="ri-user-line"></i> User
                        </>
                      )}
                    </span>
                  </td>
                  <td>{formatDate(user.createdAt)}</td>
                  <td>
                    <div className="action-buttons">
                      {user.role !== 'admin' && (
                        <button
                          className="role-toggle-btn"
                          onClick={() => toggleRole(user)}
                          title="Toggle admin role"
                        >
                          <i className="ri-shield-line"></i>
                        </button>
                      )}
                      {user.role !== 'admin' && (
                        <button
                          className="delete-btn"
                          onClick={() => setDeleteConfirm(user._id)}
                        >
                          <i className="ri-delete-bin-line"></i>
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteConfirm && (
        <div className="delete-modal">
          <div className="delete-modal-content">
            <h3>Confirm Delete</h3>
            <p>Are you sure you want to delete this user? This action cannot be undone.</p>
            <div className="modal-actions">
              <button
                className="cancel-btn"
                onClick={() => setDeleteConfirm(null)}
              >
                Cancel
              </button>
              <button
                className="confirm-delete-btn"
                onClick={() => handleDelete(deleteConfirm)}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsersManagement;

